import { smartSortEnabledByDefault } from 'config';
import links from 'helpers/links';
import sortDirectionFlow from 'helpers/sortDirectionFlow';
import { fromJS, List, Map } from 'immutable';
import { matchPath } from 'react-router';
import applicationActions from 'redux/application/actions';
import emailTemplatesActions from 'redux/emailTemplates/actions';
import hiringConfirmationActions from 'redux/hiringConfirmation/actions';
import spreatsheetActions from 'redux/spreadsheet/actions';
import teamActions from 'redux/team/actions';
import zipActions from 'redux/zip/actions';
import { ReduxAction } from 'types/redux.types';
import projectActions from './actions';

const sortOptions: Map<string, any> = Map({
  name: null,
  selectionLevel: null,
  rating: null,
  preselectionPassed: null,
  rejected: null,
  tags: null,
  suggested: null,
});

const initState = Map<string, any>({
  currentProject: null,
  projectLoading: true,
  errorLoading: false,
  accessDenied: false,
  projectNotVisible: false,
  projectHasJobWithPPP: undefined,
  projectPPPLoading: false,
  shouldLoadProjectPPP: false,
  applicationFilters: {},
  applicationListExpanded: true,
  filtersDirty: false,
  selectedFilters: Map<string, any>({
    selectionLevel: [],
    selectionLevelValue: [],
    rating: [],
    tags: [],
    skipTags: [],
    candidateName: '',
    email: undefined,
    filterByJobId: null,
    fromSite: [],
    preselectionFilter: [],
  }),
  sort: Map({
    name: null,
    selectionLevel: null,
    rating: null,
    preselectionPassed: null,
    rejected: null,
    tags: null,
    suggested: smartSortEnabledByDefault ? 'desc' : null,
  }),
  selectedApplications: Map({}),
  showBulkActions: false,
  hideApplicationList: false,
  showHidden: false,
  selectionLabelEditorVisible: false,
  candidateProfilesUnlocking: [],
  preselectionTreeSelect: Map({
    treeData: [],
    valueMap: {},
    paths: [],
  }),
  bulkTarget: null,
  statusTarget: null,
  statusMenuAutoOpenKey: null,
  viewSwitchHighlight: false,
  calendarTourTarget: null,
  excludeFromBulk: [],
  previousPathname: null,
});

export default function projectReducer(state = initState, action: ReduxAction) {
  switch (action.type) {
    case projectActions.FETCH_PROJECT_START:
      return state
        .set('projectLoading', true)
        .set('shouldLoadProjectPPP', true)
        .set('errorLoading', false);
    case projectActions.FETCH_PROJECT_DONE:
      return state
        .set('currentProject', fromJS(action.payload))
        .set('projectLoading', false)
        .set('errorLoading', false)
        .set('accessDenied', false)
        .set('projectNotVisible', false);

    case projectActions.FETCH_PROJECT_ERROR:
      return state.set('projectLoading', false).set('errorLoading', true);

    case projectActions.FETCH_PROJECT_NOT_VISIBLE:
      return state.set('projectNotVisible', true);

    case projectActions.FETCH_PROJECT_DENIED:
      return state.set('accessDenied', true);

    case hiringConfirmationActions.HIRING_ANSWER_SUBMITTED: {
      const currentProject = state.get('currentProject');
      if (!currentProject) return state;
      const { answer } = action.payload;
      const jobIdx = currentProject
        .get('jobs')
        ?.findIndex((job: any) => job.get('id') === answer.jobId);
      if (jobIdx == null || jobIdx < 0) return state;
      // Record the outcome too, not just that the prompt is done: the selection board reads it to
      // decide which jobs a not-hired answer could still be about, and to reopen an existing answer
      // pre-filled. Without it the board keeps treating the job as unanswered until the next fetch.
      return state.setIn(['currentProject', 'jobs', jobIdx, 'didYouHireFeedback'], false).setIn(
        ['currentProject', 'jobs', jobIdx, 'hiringOutcome'],
        fromJS({
          action: answer.action,
          reason: answer.reason ?? null,
          reasonText: answer.reasonText ?? null,
        })
      );
    }

    case projectActions.FETCH_PROJECT_PPP_START:
      return state.set('projectPPPLoading', true).set('shouldLoadProjectPPP', false);

    case projectActions.FETCH_PROJECT_PPP_DONE:
      return state
        .set('projectHasJobWithPPP', action.payload.projectHasJobWithPPP)
        .set('projectPPPLoading', false)
        .set('shouldLoadProjectPPP', false);

    case projectActions.FETCH_PROJECT_PPP_ERROR:
    case projectActions.FETCH_PROJECT_PPP_CANCELLED:
      return state.set('projectPPPLoading', false).set('shouldLoadProjectPPP', false);

    case projectActions.FETCH_PROJECT_PPP_LOCKED_APPLICATIONS_START:
      return state.set('projectPPPLoading', true);
    case projectActions.FETCH_PROJECT_PPP_LOCKED_APPLICATIONS_DONE:
      return state
        .set('projectPPPLoading', false)
        .set('excludeFromBulk', action.payload.pppLockedApplications || []);
    case projectActions.FETCH_PROJECT_PPP_LOCKED_APPLICATIONS_ERROR:
    case projectActions.FETCH_PROJECT_PPP_LOCKED_APPLICATIONS_CANCELLED:
      return state.set('projectPPPLoading', false).set('excludeFromBulk', []);

    case projectActions.RESET_PROJECT_PPP:
      return state
        .set('projectHasJobWithPPP', undefined)
        .set('projectPPPLoading', false)
        .set('excludeFromBulk', []);

    case projectActions.INCLUDE_IN_BULK:
      return state.update('excludeFromBulk', (excludeFromBulk: any[]) => {
        const applicationId = action.payload.applicationId;
        if (!excludeFromBulk.includes(applicationId)) {
          return excludeFromBulk;
        }
        return excludeFromBulk.filter((id) => id !== applicationId);
      });

    case projectActions.APPLICATION_FILTER_FETCHING_DONE:
      const { preselectionJobs } = action.payload;

      const formattedPreselectionJobs = preselectionJobs?.map((job: any) => ({
        value: job.jobId,
        title: job.title,
        selectable: false,
        checkable: false,
        key: job.jobId,
        children: job.questions.map((question: any) => ({
          value: question.questionId,
          title: question.question,
          selectable: false,
          checkable: false,
          key: question.questionId,
          children: question.answers.map((answer: any) => ({
            value: answer.answerId,
            title: answer.answer,
          })),
        })),
      }));

      const valueMap: Record<string, any> = {};

      const buildValueMap = (nodes: any[], parent?: any) => {
        return nodes?.map(({ value, children }) => {
          const node: any = (valueMap[value] = { parent, value });
          node.children = buildValueMap(children, node);
          return node;
        });
      };

      buildValueMap(formattedPreselectionJobs);

      return state
        .set('applicationFilters', action.payload)
        .setIn(['preselectionTreeSelect', 'treeData'], formattedPreselectionJobs)
        .setIn(['preselectionTreeSelect', 'valueMap'], valueMap);

    /**
     * cycle sort
     */
    case projectActions.SET_SORT: {
      const newState = sortOptions.set(
        action.payload.field,
        sortDirectionFlow(action.payload.currentDirection) as string
      );
      return state.set('sort', newState).set('projectLoading', true);
    }

    case projectActions.SET_FILTER:
      const { group, filters } = action.payload;

      if (group === 'filterByJobId' || group === 'fromSite') {
        const updatedGroup = group === 'filterByJobId' ? 'fromSite' : 'filterByJobId';

        /**
         * if transitioning to a job that has smart sort disabled, set the suggested sort to null
         * otherwise, keep the suggested sort as is
         */
        let jobSmartSortEnabled = undefined;
        if (group === 'filterByJobId') {
          const jobId = filters;
          const currentProject = state.get('currentProject');
          const job =
            currentProject && jobId
              ? currentProject.get('jobs').find((j: any) => j.get('id') === jobId)
              : null;

          jobSmartSortEnabled = job?.getIn(['jobSmartSort', 'enabled']) || false;
        }

        return state
          .set('filtersDirty', true)
          .setIn(['selectedFilters', updatedGroup], group === 'filterByJobId' ? [] : null)
          .setIn(['selectedFilters', group], filters)
          .set('projectLoading', true)
          .setIn(['selectedFilters', 'preselectionPassed'], undefined)
          .setIn(
            ['sort', 'suggested'],
            jobSmartSortEnabled === false ? null : state.getIn(['sort', 'suggested'])
          );
      }

      if (group === 'preselectionFilter') {
        const valueMap = state.getIn(['preselectionTreeSelect', 'valueMap']);

        const getPaths = (value: any): any[] => {
          const node = valueMap[value];
          if (!node.children || node.children.length === 0) {
            const path = [];
            let current = node;
            while (current) {
              path.unshift(current.value);
              current = current.parent;
            }
            return [path];
          } else {
            return node.children.flatMap((child: any) => getPaths(child.value));
          }
        };

        const allPaths = filters.flatMap((value: any) => getPaths(value));

        return state
          .set('filtersDirty', true)
          .setIn(['selectedFilters', group], filters)
          .set('projectLoading', true)
          .setIn(['preselectionTreeSelect', 'paths'], allPaths);
      }

      if (group === 'rating') {
        if (filters.some((rating: string) => rating.includes('employer-score'))) {
          let newState = state
            .set('filtersDirty', true)
            .setIn(['selectedFilters', 'rating'], [filters[filters.length - 1]])
            .set('projectLoading', true);

          if (!state.getIn(['sort', 'rating'])) {
            newState = newState.setIn(['sort', 'rating'], 'desc');
          }

          return newState;
        }
      }

      if (['tags', 'skipTags'].includes(group)) {
        const otherGroup = group === 'tags' ? 'skipTags' : 'tags';

        // normalize incoming filters to JS array
        const incomingFilters = Array.isArray(filters)
          ? filters
          : filters && typeof filters.toJS === 'function'
            ? filters.toJS()
            : filters
              ? [filters]
              : [];

        // current value of the other group (could be JS array or Immutable List)
        const currentOther = state.getIn(['selectedFilters', otherGroup]);
        let currentOtherArr = currentOther;
        if (currentOther && typeof currentOther.toJS === 'function') {
          currentOtherArr = currentOther.toJS();
        }
        if (!Array.isArray(currentOtherArr)) {
          currentOtherArr = currentOtherArr ? [currentOtherArr] : [];
        }

        // remove any tags in `incomingFilters` from the other group
        const newOther = (currentOtherArr || []).filter((t: any) => !incomingFilters.includes(t));

        return state
          .set('filtersDirty', true)
          .set('projectLoading', true)
          .setIn(['selectedFilters', group], incomingFilters)
          .setIn(['selectedFilters', otherGroup], newOther);
      }

      return state
        .set('filtersDirty', true)
        .setIn(['selectedFilters', group], filters)
        .set('projectLoading', true);

    case projectActions.RESET_FILTER:
      return state
        .set(
          'selectedFilters',
          initState
            .get('selectedFilters')
            .set('filterByJobId', state.getIn(['selectedFilters', 'filterByJobId']))
            .set('fromSite', state.getIn(['selectedFilters', 'fromSite']))
        )
        .setIn(['preselectionTreeSelect', 'paths'], [])
        .set('projectLoading', true);

    case projectActions.RESET_SELECTED_JOB:
      return state
        .setIn(['selectedFilters', 'filterByJobId'], null)
        .setIn(['selectedFilters', 'fromSite'], []);

    case projectActions.RESET_APPLICATION_SELECTION:
      return state.set('selectedApplications', Map({}));

    /**
     * toggle visiblity of bulk actions bar
     */
    case projectActions.TOGGLE_BULK_ACTIONS:
      return state.set('showBulkActions', action.payload.state);

    /**
     * modifiy application selection
     * bulk action bar is triggered from saga as a side effect
     */
    case projectActions.MODIFY_APPLICATION_SELECTION:
      if (action.payload.checked) {
        return state.setIn(
          ['selectedApplications', action.payload.application.get('id')],
          action.payload.application
        );
      }
      return state.removeIn(['selectedApplications', action.payload.application.get('id')]);

    case projectActions.SELECT_MULTIPLE_APPLICATIONS: {
      let newApplicationList = Map({});

      action.payload.applications.map((singleApp: any) => {
        newApplicationList = newApplicationList.set(singleApp.id, Map(singleApp));
      });

      return state.set('selectedApplications', Map(newApplicationList));
    }

    case projectActions.SELECT_ALL_APPLICATIONS: {
      let selectedApplications = Map({});
      state.getIn(['currentProject', 'applications']).forEach((item: any) => {
        state.get('showHidden');
        /**
         * select only with consent and not hidden OOOOR select hidden if showHidden set
         */
        if (
          item.get('consent') &&
          (!item.get('hidden') || state.get('showHidden')) &&
          !state.get('excludeFromBulk').includes(item.get('id'))
        ) {
          selectedApplications = selectedApplications.set(item.get('id'), item);
        }
      });
      return state.set('selectedApplications', selectedApplications);
    }

    case '@@router/LOCATION_CHANGE': {
      const {
        payload: {
          location: { pathname },
        },
      } = action;
      let newState = state;

      // clean dirtiness if going to any but those 4/5 pages
      const previousPathname = newState.get('previousPathname');
      if (previousPathname) {
        const doesPreviousMatch: any = matchPath(previousPathname, {
          path: links.application().pattern,
          exact: true,
          strict: false,
        });

        if (doesPreviousMatch) {
          const preserveDirtinessOnThesePaths = [
            links.selection().pattern,
            links.jobSettings().pattern,
            links.statistics().patternProject,
            links.project().pattern,
            links.jobOrderReceipts().pattern,
          ];

          const goingToPreserved = !!preserveDirtinessOnThesePaths.filter(
            (path) => !!matchPath(location.pathname, path)
          ).length;

          if (!goingToPreserved) {
            newState = newState.set('filtersDirty', false);
          }
        }
      }

      // expand list if navigating to whole project from single application
      const match: any = matchPath(location.pathname, {
        path: links.application().pattern,
        exact: true,
        strict: false,
      });

      if (match && match.params.projectId && !match.params.applicationId) {
        newState = newState.set('applicationListExpanded', true);
      }
      newState = newState.set('previousPathname', pathname);
      return newState;
    }

    case teamActions.MUTATE_REVOKE_DONE:
    case teamActions.MUTATE_GRANT_DONE: {
      let newState = state;

      /** if viewing single job update there */
      if (state.get('currentProject')) {
        newState = newState.setIn(['currentProject', 'access'], fromJS(action.payload.access));
      }

      return newState;
    }

    case applicationActions.APPLICATION_RELEVANCE_FEEDBACK_MUTATED: {
      let newState = state;
      newState = newState.setIn(
        [
          'currentProject',
          'applications',
          state
            .getIn(['currentProject', 'applications'])
            .findIndex((el: any) => el.get('id') === action.payload.applicationId),
          'feedback',
        ],
        action.payload.type
      );
      return newState;
    }

    case applicationActions.APPLICATION_ANONYMIZED_STATE_CHANGED: {
      const index = (state.getIn(['currentProject', 'applications']) ?? List()).findIndex(
        (el: any) => el.get('id') === action.payload.applicationId
      );
      return index === -1
        ? state
        : state.setIn(
            ['currentProject', 'applications', index, 'anonymizedApplicationState'],
            action.payload.state
          );
    }

    /**
     * when we change selectionLevel it needs to be updated in applications for project
     */
    case applicationActions.APPLICATION_SELECTION_LEVEL_MUTATION_START:
    case applicationActions.APPLICATION_SELECTION_LEVEL_MUTATION_DONE: {
      let newState = state;
      Object.keys(action.payload.id).forEach((key) => {
        const index = state
          .getIn(['currentProject', 'applications'])
          .findIndex((el: any) => el.get('id') === action.payload.id[key]);
        if (index === -1) return;
        // Callers send the integer selectionLevelValue; update it optimistically so the board
        // regroups the card into the new column (instead of snapping back) and list dropdowns
        // reflect the new level immediately, before the server refetch arrives. Keep the
        // deprecated enum in sync when it is provided.
        if (
          action.payload.selectionLevelValue !== undefined &&
          action.payload.selectionLevelValue !== null
        ) {
          newState = newState.setIn(
            ['currentProject', 'applications', index, 'selectionLevelValue'],
            action.payload.selectionLevelValue
          );
        }
        if (action.payload.selectionLevel !== undefined) {
          newState = newState.setIn(
            ['currentProject', 'applications', index, 'selectionLevel'],
            action.payload.selectionLevel
          );
        }
      });
      return newState;
    }

    case projectActions.UPDATE_REJECTED_APPLICATIONS: {
      let newState = state;
      Object.keys(action.payload.id).forEach((key) => {
        newState = newState.setIn(
          [
            'currentProject',
            'applications',
            state
              .getIn(['currentProject', 'applications'])
              .findIndex((el: any) => el.get('id') === action.payload.id[key]),
            'rejected',
          ],
          true
        );
      });
      return newState;
    }

    case projectActions.SHOW_APPLICATIONS_LIST:
      return state.set('hideApplicationList', false);

    case projectActions.HIDE_APPLICATIONS_LIST:
      return state.set('hideApplicationList', true);

    case applicationActions.APPLICATION_FETCH:
      return (
        state.getIn(['currentProject', 'applications'])
          ? state.setIn(
              [
                'currentProject',
                'applications',
                state
                  .getIn(['currentProject', 'applications'])
                  .findIndex((el: any) => el.get('id') === action.payload.variables.applicationId),
                'employerView',
              ],
              true
            )
          : state
      ).set('hideApplicationList', true);

    case projectActions.TOGGLE_SHOW_HIDDEN:
      return state.set('showHidden', action.payload);

    /**
     * application list collapse/expand
     */
    case projectActions.EXPAND_APPLICATIONS_LIST:
      return state.set('applicationListExpanded', true);
    case projectActions.COLLAPSE_APPLICATIONS_LIST:
      return state.set('applicationListExpanded', false);

    case applicationActions.APPLICATION_HIDE_START:
      return state.getIn(['currentProject', 'applications'])
        ? state.setIn(
            [
              'currentProject',
              'applications',
              state.getIn(['currentProject', 'applications']).findIndex((el: any) => {
                return el.get('id') === action.payload.applicationId;
              }),
              'hidden',
            ],
            action.payload.state
          )
        : state;

    /**
     * when we change rating it needs to be updated in applications for job
     */
    case applicationActions.APPLICATION_RATING_MUTATION_START:
    case applicationActions.APPLICATION_RATING_MUTATION_DONE: {
      let newState = state;
      /**
       * check that user is still looking at project
       */
      if (state.getIn(['currentProject', 'applications'])) {
        Object.keys(action.payload.id).forEach((key) => {
          newState = newState.setIn(
            [
              'currentProject',
              'applications',
              state
                .getIn(['currentProject', 'applications'])
                .findIndex((el: any) => el.get('id') === action.payload.id[key]),
              'rating',
            ],
            action.payload.rating
          );
        });
      }
      return newState;
    }

    case zipActions.ZIP_APPLICATIONS_DONE:
    case spreatsheetActions.SPREADSHEET_GENERATE_DONE: {
      let newState = state;
      action.payload.applicationIds.forEach((applicationId: string) => {
        newState = newState.getIn(['currentProject', 'applications'])
          ? newState.setIn(
              [
                'currentProject',
                'applications',
                newState
                  .getIn(['currentProject', 'applications'])
                  .findIndex((el: any) => el.get('id') === parseInt(applicationId, 10)),
                'employerView',
              ],
              true
            )
          : newState;
      });
      return newState;
    }

    case projectActions.TOGGLE_SELECTION_LABEL_EDITOR:
      return state.set('selectionLabelEditorVisible', action.payload);

    case projectActions.CHANGE_SELECTION_LABEL_TEXT: {
      const customSelectionLabels = state.getIn(['currentProject', 'customSelectionLabels']).toJS();
      const index = customSelectionLabels.findIndex(
        (item: any) => item.selectionLevel === action.payload.selectionLevel
      );
      const exists = index !== -1;
      if (exists) {
        return state.setIn(
          [
            'currentProject',
            'customSelectionLabels',
            state
              .getIn(['currentProject', 'customSelectionLabels'])
              .findIndex(
                (item: any) => item.get('selectionLevel') === action.payload.selectionLevel
              ),
            'name',
          ],
          action.payload.text
        );
      }

      customSelectionLabels.push({
        selectionLevel: action.payload.selectionLevel,
        name: action.payload.text,
      });

      return state.setIn(
        ['currentProject', 'customSelectionLabels'],
        fromJS(customSelectionLabels)
      );
    }

    case projectActions.CHANGE_SELECTION_LABEL_VISIBLITY: {
      const customSelectionLabels = state.getIn(['currentProject', 'customSelectionLabels']).toJS();
      const index = customSelectionLabels.findIndex(
        (item: any) => item.selectionLevel === action.payload.selectionLevel
      );
      const exists = index !== -1;
      if (exists) {
        return state.setIn(
          [
            'currentProject',
            'customSelectionLabels',
            state
              .getIn(['currentProject', 'customSelectionLabels'])
              .findIndex(
                (item: any) => item.get('selectionLevel') === action.payload.selectionLevel
              ),
            'hidden',
          ],
          action.payload.hidden
        );
      }

      customSelectionLabels.push({
        selectionLevel: action.payload.selectionLevel,
        hidden: action.payload.hidden,
      });

      return state.setIn(
        ['currentProject', 'customSelectionLabels'],
        fromJS(customSelectionLabels)
      );
    }

    case projectActions.SAVE_CUSTOM_SELECTION_LABELS_DONE: {
      /* prevent modification if user navigate away from this job */
      log(action.payload);
      const projectId = state.getIn(['currentProject', 'id']);
      // Bump a settle counter on every save resolution so consumers (the selection-levels drawer's
      // optimistic seed) can drop their optimistic state even on rename/hide-only saves — those don't
      // change the persisted level set, so keying off the columns alone would never reconcile.
      const bumped = state.set(
        'selectionLabelsSaveNonce',
        ((state.get('selectionLabelsSaveNonce') as number) || 0) + 1
      );
      if (projectId === action.payload.id) {
        return bumped
          .setIn(
            ['currentProject', 'customSelectionLabels'],
            fromJS(action.payload.customSelectionLabels)
          )
          .setIn(['currentProject', 'hiredColumnRenamed'], action.payload.hiredColumnRenamed)
          .setIn(['currentProject', 'selectionLevels'], fromJS(action.payload.selectionLevels));
      }
      return bumped;
    }

    case projectActions.SAVE_CUSTOM_SELECTION_LABELS_ERROR:
      // Same settle bump on failure so the drawer drops its optimistic seed and reverts to the
      // persisted (authoritative) rows instead of showing the failed edit as if it were saved.
      return state.set(
        'selectionLabelsSaveNonce',
        ((state.get('selectionLabelsSaveNonce') as number) || 0) + 1
      );

    case projectActions.FETCH_SELECTION_LEVEL_CONFIG_DONE: {
      if (state.getIn(['currentProject', 'id']) === action.payload.id) {
        return state
          .setIn(['currentProject', 'selectionLevels'], fromJS(action.payload.selectionLevels))
          .setIn(
            ['currentProject', 'customSelectionLabels'],
            fromJS(action.payload.customSelectionLabels)
          )
          .setIn(['currentProject', 'hiredColumnRenamed'], action.payload.hiredColumnRenamed);
      }
      return state;
    }

    case emailTemplatesActions.EMAIL_SEND_START: {
      let newState = state;
      Object.keys(action.payload.recepients).forEach((key) => {
        const recepient = action.payload.recepients[key];
        const events = state.getIn([
          'currentProject',
          'applications',
          state
            .getIn(['currentProject', 'applications'])
            .findIndex((el: any) => el.get('id') === recepient.id),
          'events',
        ]);
        newState = newState.setIn(
          [
            'currentProject',
            'applications',
            state
              .getIn(['currentProject', 'applications'])
              .findIndex((el: any) => el.get('id') === recepient.id),
            'events',
          ],
          events.push(
            Map({
              eventType: `${action.payload.category}_sent`,
            })
          )
        );
      });
      return newState;
    }

    case applicationActions.APPLICATION_MARK_AS_UNREAD_START:
      return state.getIn(['currentProject', 'applications'])
        ? state.setIn(
            [
              'currentProject',
              'applications',
              state.getIn(['currentProject', 'applications']).findIndex((el: any) => {
                return el.get('id') === action.payload.id;
              }),
              'employerView',
            ],
            false
          )
        : state;

    case applicationActions.APPLICATION_COMMENT_EDIT_MUTATION_DONE:
    case applicationActions.APPLICATION_COMMENT_ADD_MUTATION_DONE:
      return state.getIn(['currentProject', 'applications'])
        ? state.setIn(
            [
              'currentProject',
              'applications',
              state.getIn(['currentProject', 'applications']).findIndex((el: any) => {
                return el.get('id') === action.payload.id;
              }),
              'comments',
            ],
            fromJS(action.payload.comments)
          )
        : state;

    case applicationActions.APPLICATION_TAG_MUTATION_ADD_DONE:
    case applicationActions.APPLICATION_TAG_MUTATION_REMOVE_DONE:
      return state.getIn(['currentProject', 'applications'])
        ? state.setIn(
            [
              'currentProject',
              'applications',
              state.getIn(['currentProject', 'applications']).findIndex((el: any) => {
                return el.get('id') === action.payload.id;
              }),
              'tags',
            ],
            fromJS(action.payload.tags)
          )
        : state;

    case projectActions.PROJECT_SMART_SORT_PROCESSING_REFETCH_DONE: {
      return state.getIn(['currentProject', 'id']) === action.payload.id
        ? state.setIn(
            ['currentProject', 'projectSmartSortProcessingStatus'],
            fromJS(action.payload.projectSmartSortProcessingStatus)
          )
        : state;
    }

    case projectActions.APPLICATION_RELEVANCE_REFRESH_DONE:
      return state.getIn(['currentProject', 'applications'])
        ? state
            .setIn(
              [
                'currentProject',
                'applications',
                state.getIn(['currentProject', 'applications']).findIndex((el: any) => {
                  return el.get('id') === action.payload.id;
                }),
                'relevance',
              ],
              fromJS(action.payload.relevance)
            )
            .setIn(
              [
                'currentProject',
                'applications',
                state.getIn(['currentProject', 'applications']).findIndex((el: any) => {
                  return el.get('id') === action.payload.id;
                }),
                'processingStatus',
              ],
              fromJS(action.payload.processingStatus)
            )
            .setIn(
              [
                'currentProject',
                'applications',
                state.getIn(['currentProject', 'applications']).findIndex((el: any) => {
                  return el.get('id') === action.payload.id;
                }),
                'summary',
              ],
              fromJS(action.payload.summary)
            )
            .setIn(
              [
                'currentProject',
                'applications',
                state.getIn(['currentProject', 'applications']).findIndex((el: any) => {
                  return el.get('id') === action.payload.id;
                }),
                'feedback',
              ],
              fromJS(action.payload.feedback)
            )
        : state;

    case applicationActions.APPLICATION_COMMENTS_FETCH_DONE: {
      const applications = state.getIn(['currentProject', 'applications']);
      if (!applications) {
        // If there are no applications, return the current state.
        return state;
      }

      const index = applications.findIndex((el: any) => el.get('id') === action.payload.id);
      if (index === -1) {
        // If the application with the specified ID is not found, return the current state.
        return state;
      }

      // Update the comments of the application at the specified index.
      return state.setIn(
        ['currentProject', 'applications', index, 'comments'],
        fromJS(action.payload.comments)
      );
    }

    case applicationActions.APPLICATION_RATING_FETCH_DONE:
      const applications = state.getIn(['currentProject', 'applications']);
      if (!applications) {
        // If there are no applications, return the current state.
        return state;
      }

      const index = applications.findIndex((el: any) => el.get('id') === action.payload.id);
      if (index === -1) {
        // If the application with the specified ID is not found, return the current state.
        return state;
      }

      // Update the rating of the application at the specified index.
      return state.setIn(
        ['currentProject', 'applications', index, 'rating'],
        action.payload.rating
      );

    case applicationActions.APPLICATION_EVENTS_FETCH_DONE: {
      const applications = state.getIn(['currentProject', 'applications']);
      if (!applications) {
        // If there are no applications, return the current state.
        return state;
      }

      const index = applications.findIndex((el: any) => el.get('id') === action.payload.id);
      if (index === -1) {
        // If the application with the specified ID is not found, return the current state.
        return state;
      }

      // Update the events of the application at the specified index.
      return state.setIn(['currentProject', 'applications', index], fromJS(action.payload));
    }

    case applicationActions.APPLICATION_SELECTION_LEVEL_FETCH_DONE: {
      const applications = state.getIn(['currentProject', 'applications']);
      if (!applications) {
        // If there are no applications, return the current state.
        return state;
      }

      const index = applications.findIndex((el: any) => el.get('id') === action.payload.id);
      if (index === -1) {
        // If the application with the specified ID is not found, return the current state.
        return state;
      }

      // Realtime refresh: the board groups by the integer selectionLevelValue, so write it (mirroring
      // the optimistic MUTATION handler) so the card regroups into the correct column instead of
      // staying on the stale value. Keep the deprecated enum in sync for legacy consumers.
      let updated = state;
      if (
        action.payload.selectionLevelValue !== undefined &&
        action.payload.selectionLevelValue !== null
      ) {
        updated = updated.setIn(
          ['currentProject', 'applications', index, 'selectionLevelValue'],
          action.payload.selectionLevelValue
        );
      }
      return updated.setIn(
        ['currentProject', 'applications', index, 'selectionLevel'],
        action.payload.selectionLevel
      );
    }

    case projectActions.TOGGLE_CANDIDATE_PROFILE_UNLOCKING:
      const candidateProfileId = action.payload;
      const candidateProfilesUnlocking = state.get('candidateProfilesUnlocking');
      const updatedUnlocking = candidateProfilesUnlocking.includes(candidateProfileId)
        ? candidateProfilesUnlocking.filter((id: number) => id !== candidateProfileId)
        : candidateProfilesUnlocking.concat(candidateProfileId);

      return state.set('candidateProfilesUnlocking', updatedUnlocking);

    case projectActions.SET_BULK_TARGET:
      return state.set('bulkTarget', action.payload.target);

    case projectActions.SET_STATUS_TARGET:
      return state.set('statusTarget', action.payload.target);

    case projectActions.SET_STATUS_MENU_AUTO_OPEN:
      return state.set('statusMenuAutoOpenKey', action.payload.key);

    case projectActions.SET_VIEW_SWITCH_HIGHLIGHT:
      return state.set('viewSwitchHighlight', action.payload.active);

    case projectActions.SET_CALENDAR_TOUR_TARGET:
      return state.set('calendarTourTarget', action.payload.target);

    case projectActions.SET_FILTER_DIRTINESS:
      return state.set('filtersDirty', action.payload);

    case 'LOGOUT':
      return initState;
    default:
      return state;
  }
}
